import { ArrowRight } from "lucide-react"
import { pluralize } from "@/shared/lib/pluralize"
import { HapticLink } from "@/shared/ui/haptic-link"
import { Skeleton } from "@/shared/ui/skeleton"
import { useBeginnerProgress } from "../api/progress"
import { beginnerSteps } from "../model/beginner-path"
import { MountainIllustration } from "./mountain-illustration"

function promoLabels(done: number, total: number): { subtitle: string; action: string } {
	if (done === 0) {
		return {
			subtitle: "От нуля до первых подписчиков",
			action: pluralize(total, ["шаг", "шага", "шагов"]),
		}
	}
	if (done >= total) {
		return { subtitle: "Путь пройден целиком", action: "Пересмотреть" }
	}
	return { subtitle: `Пройдено ${done} из ${total}`, action: "Продолжить" }
}

export function BeginnerPathPromo() {
	const { data: completed = [], isLoading } = useBeginnerProgress()
	const total = beginnerSteps.length
	const done = Math.min(completed.length, total)
	const { subtitle, action } = promoLabels(done, total)

	return (
		<HapticLink
			to="/beginner-path"
			data-tour="beginner"
			className="group relative block min-h-40 overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-primary/15 p-5 ring-1 ring-white/10 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			<MountainIllustration className="absolute right-0 bottom-0 h-full w-[52%]" />

			<div className="relative max-w-[56%]">
				<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
					Старт здесь
				</p>
				<p className="mt-1 text-2xl leading-tight font-bold tracking-tight">Путь новичка</p>
				{isLoading ? (
					<Skeleton className="mt-2 h-4 w-32" />
				) : (
					<p className="mt-1.5 text-sm leading-snug text-muted-foreground">{subtitle}</p>
				)}
				{!isLoading && done > 0 ? (
					<span aria-hidden className="mt-2.5 block h-1 overflow-hidden rounded-full bg-white/10">
						<span
							className="block h-full rounded-full bg-primary"
							style={{ width: `${Math.round((done / total) * 100)}%` }}
						/>
					</span>
				) : null}
				{isLoading ? (
					<Skeleton className="mt-4 h-9 w-36 rounded-full" />
				) : (
					<span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25">
						{action}
						<ArrowRight className="size-4 transition-transform group-active:translate-x-0.5" />
					</span>
				)}
			</div>
		</HapticLink>
	)
}
